/* eslint-disable import/prefer-default-export */
import { isStoreApiGatewayFailure, isStoreApiNetworkFailure } from './buildAxiosErrorMeta';

// Single-active, injectable API-error toaster. Decoupled from the toast/translation globals
// so the "never stack duplicate toasts" gating + class routing can be unit-tested.
//   toast: the toast singleton — toast.error(msg, { timeout, onClose }) returns an id
//   t:     the translation fn (__)
//
// Three classes, mapped to two gated sticky toasts (see api.js for the Bugsnag bucket story):
//   NETWORK (no HTTP response) + GATEWAY (HTML 5xx from edge) → one "connection problem" toast
//   SERVER (real API error body)                             → one "contact support" toast
export function createApiErrorToasts(toast, t) {
  let activeNetworkToastId = null;
  let activeServerToastId = null;

  function toastNetworkConnection() {
    if (activeNetworkToastId !== null) {
      return;
    }

    activeNetworkToastId = toast.error(t('error_api_network_connection'), {
      timeout: false,
      onClose: () => { activeNetworkToastId = null; },
    });
  }

  function toastStoreServerFailure() {
    if (activeServerToastId !== null) {
      return;
    }

    activeServerToastId = toast.error(t('error_tasks_api_operation'), {
      timeout: false,
      onClose: () => { activeServerToastId = null; },
    });
  }

  function toastInternalApiServerFailure(error) {
    const failUrl = error.config?.url;

    // File-size limit (4005) is a specific, actionable validation error — its own short
    // toast, kept out of the generic server gate so it isn't hidden by an open server toast.
    if (failUrl?.includes('/attachment') && error.response?.data?.code === 4005) {
      toast.error(t('fileUpload_limitStore', { limit: '' }));

      return;
    }

    if (failUrl?.includes('/ganttview')
      || failUrl?.includes('/resources')
      || failUrl?.includes('/custom_columns')
      || failUrl?.includes('/comments')
      || failUrl?.includes('/attachment')) {
      toastStoreServerFailure();
    }
  }

  function toastApiServerFailure(error) {
    const failUrl = error.config?.url;

    // Single OR-chain (not separate ifs): /tasks/custom_columns matches two fragments
    // (/task + /custom_columns) but must still toast only once.
    if (failUrl?.includes('/task')
      || failUrl?.includes('/link')
      || failUrl?.includes('/resources')
      || failUrl?.includes('/custom_columns')
      || failUrl?.includes('/history')) {
      toastStoreServerFailure();
    }
  }

  function isConnectivityFailure(error) {
    return isStoreApiNetworkFailure(error) || isStoreApiGatewayFailure(error);
  }

  function onInternalApiError(error) {
    if (isConnectivityFailure(error)) {
      toastNetworkConnection();
    } else {
      toastInternalApiServerFailure(error);
    }
  }

  function onApiError(error) {
    if (isConnectivityFailure(error)) {
      toastNetworkConnection();
    } else {
      toastApiServerFailure(error);
    }
  }

  return { onInternalApiError, onApiError };
}
